import React, { useEffect, useState } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import styled from 'styled-components';
import logger from '../../utils/logger';

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background};
`;

const Spinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid ${({ theme }) => theme.colors.primary};
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

/**
 * Composant qui protège les routes nécessitant une authentification
 * Redirige vers la page de connexion si l'utilisateur n'est pas connecté
 */
const ProtectedRoute: React.FC = () => {
  const { supabaseUser, loading, session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Effet pour rediriger vers la page de connexion si l'utilisateur n'est pas connecté
  useEffect(() => {
    const checkAuth = async () => {
      // Attendre que le chargement initial soit terminé
      if (loading) {
        return;
      }
      
      logger.debug('Vérification de l\'authentification pour route protégée:', { 
        path: location.pathname,
        isAuthenticated: !!supabaseUser,
        hasSession: !!session
      });
      
      // Si l'utilisateur n'est pas authentifié, rediriger vers la page de connexion
      if (!supabaseUser || !session) {
        logger.info('Redirection vers la page de connexion (non authentifié)');
        navigate('/login', { 
          replace: true,
          state: { from: location.pathname } // Stocker l'URL d'origine pour rediriger après connexion
        });
      }
      
      // Fin de la vérification
      setCheckingAuth(false);
    };
    
    checkAuth();
  }, [supabaseUser, loading, navigate, location.pathname, session]);

  // Afficher un spinner pendant le chargement
  if (loading || checkingAuth) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  // Si l'utilisateur n'est pas connecté, ne rien afficher (la redirection se fera via useEffect)
  if (!supabaseUser || !session) {
    return null;
  }

  // Afficher le contenu protégé si l'utilisateur est connecté
  return <Outlet />;
};

export default ProtectedRoute;

